import type {
  RedeemBatchCreateResult,
  RedeemCodeList,
  RedeemCodeStatus,
  RedeemResult,
} from '@/types/redeemCodes';

/**
 * RedeemCodesService — 序號兌換點數(R276 建;R277 對帳 BE docs/99 真契約)
 * -----------------------------------------------------------------------------
 * 四支端點語意(SoT = BE `docs/99-frontend-redeem-code.md`):
 *
 *   - `redeem`:兌一張序號 → 面值點數存入**當下工作區**錢包(`X-Current-Group`)。
 *     **冪等在 BE(per user × code)**:同一人重送同一碼 = 200 + 首次的結果,不重複
 *     入帳 —— 網路逾時**直接重送即可**,FE 不做防重、不帶 idempotency header
 *     (docs/99 §0-2 明令「不要自己做防重」)。
 *   - `adminGenerate`:批次產碼(count 1..500 × credits 1..9999;note ≤200 /
 *     expires_at 選填)。碼**明文存庫**,清單端點隨時撈得回 —— 但產完當場就要給
 *     複製出口(那是發放動線)。⚠️ 這支**沒有** BE 冪等:重按 = 再鑄一批,FE 用
 *     busy 閘 + 確認語意擋 double-click。
 *   - `adminList`:歷史清單(`created_at DESC`;status / batch_id 篩選;cursor 分頁;
 *     `batch_stats` 只在帶 `batchId` 時回)。「被誰用」給 email(uid 不外流)。
 *   - `adminVoid`:作廢(冪等;已被兌換 → 409 `REDEEM_CODE_ALREADY_USED`,點已派出
 *     救不回)。
 *
 * 刻意**沒有**「查這張碼有效嗎」的預覽端點(枚舉防護)—— 要驗只能真的兌。
 */
export interface RedeemCodesService {
  /**
   * `POST /api/redeem` — 兌換一張序號。輸入容錯在 BE(strip 空白/dash、轉大寫、
   * O→0 / I→1 / L→1 救援),FE 原樣送 trim 過的字串。
   *
   * @throws ApiHttpError:422 `REDEEM_CODE_INVALID_FORMAT` / 404 `RESOURCE_NOT_FOUND`
   *   (刻意通用 — 防枚舉)/ 409 `REDEEM_CODE_ALREADY_USED`(被**別人**兌走)/
   *   409 `REDEEM_CODE_VOID` / 410 `REDEEM_CODE_EXPIRED` /
   *   429 `REDEEM_LOCKED`(連續失敗 20 次鎖 1 小時;`error.details.retry_after_seconds`)/
   *   400 `CURRENT_GROUP_REQUIRED`
   */
  redeem(code: string): Promise<RedeemResult>;

  /**
   * `POST /api/admin/redeem-codes` — 批次產碼(role=admin;BE `require_role` 403 兜底)。
   *
   * `pointsValidDays` = 兌換後點數使用效期(天;R391/docs/99 D8):BE 缺 = 預設 30、
   * 上限 365;**絕不可送 `null`**(422,沒有永久選項)—— 型別刻意是 `number | undefined`。
   *
   * `pointsValidMinutes` = 分鐘級自訂效期(R391.2 已對帳 BE D9,tag v22-122):
   * 1–525600,失效瞬間 = 兌換時刻 + N 分(不對齊午夜)。與 `pointsValidDays` **互斥**
   * —— ⚠️ BE 用 `model_fields_set` 判:minutes 有值時 **days 鍵不得出現在 body**
   * (帶了就 422,哪怕值是預設 30);FE 保證單送,兩者都帶是 caller bug。
   */
  adminGenerate(req: {
    credits: number;
    count: number;
    note?: string;
    expiresAt?: string;
    pointsValidDays?: number;
    pointsValidMinutes?: number;
  }): Promise<RedeemBatchCreateResult>;

  /** `GET /api/admin/redeem-codes` — 歷史清單(新→舊;limit 1..200 預設 50)。 */
  adminList(opts?: {
    batchId?: string;
    status?: RedeemCodeStatus;
    limit?: number;
    cursor?: string | null;
  }): Promise<RedeemCodeList>;

  /** `POST /api/admin/redeem-codes/{code}/void` — 作廢(冪等;code 可帶 dash)。 */
  adminVoid(code: string): Promise<void>;
}

/**
 * 序號兌換後端是否已交付 —— **整個功能的唯一開關**(R213 `*_SUPPORTED` 慣例)。
 *
 * ✅ **R277(2026-08-21)翻 `true`**,取證三件:
 *   1. BE tag `dev-update-v17-1`,pipeline **#6601 `deploy-dev: success`**
 *      (beta 的 deploy 是 manual 鈕,等 Bryan 按)。
 *   2. wire 已逐欄對帳 BE `app/redeem/schemas.py`(docs/99;R276 的建議形狀已全數
 *      改成真契約 —— 路徑 `/api/redeem`、admin codes 是字串清單、七個錯誤碼)。
 *   3. `RedeemCodeItem.model_fields` 已收進契約鎖 snapshot(`redeem.*`)。
 *
 * ⚠️ BE MR !62 尚未併回 main(dev 靠 tag 已生效)—— 契約鎖 freshness 對 BE 主
 * checkout 跑會顯 redeem 段缺失的 diff,屬預期,!62 併入後自動轉綠。
 * 要整域退場(BE 下架)就把這裡翻 `false`:兩個入口一起消失,不必動元件。
 */
export const REDEEM_CODES_SUPPORTED = true;

/**
 * 產碼「點數使用效期」欄(`points_valid_days`)後端是否已交付(R391,2026-09-25)。
 *
 * ✅ 開 `true`,取證(R351.2 紀律 — 標明驗到哪一層):
 *   1. BE branch `claude/redeem-points-expiry-handoff-a25050` HEAD `8bf008d7` 已含在
 *      tag **`dev-update-v22-120`**(origin 已有該 tag;`app/redeem/{models,schemas,
 *      repository}.py` 皆有 `points_valid_days`)—— 與 R277 翻 `REDEEM_CODES_SUPPORTED`
 *      同型:dev 靠 tag 生效、MR 未併 main。
 *   2. BE handoff(docs/99 §3.1 2026-09-25 段,D8)明言已 ship:缺 = 預設 30、上限
 *      365、`null` → 422(沒有永久選項)。
 *   ⚠️ 驗到「tag 內容 + handoff 宣告」這一層;deploy pipeline 終態與 HTTP 回應**未驗**。
 *   ⚠️ BE `main` 未併 → 契約鎖 snapshot 尚無這批欄(方向 BE ⊆ FE,FE 先鏡射不會紅;
 *      BE 併 main 後跑 `contract:dump` 自動補齊)。
 *
 * 風險與退場:未帶效期版的 BE 對未知 body 欄是 422 `extra="forbid"`(R292.8 族)——
 * **beta / prod 要等 BE 同批部署**才能在那裡產碼;若得先上一個沒有效期 BE 的環境,
 * 把這裡翻 `false`:效期選單整個不渲染、request 不帶欄(回到 BE 預設語意),不必動元件。
 */
export const REDEEM_POINTS_VALID_DAYS_SUPPORTED = true;

/**
 * 產碼「**分鐘級**自訂效期」後端是否已交付(R391.1 鋪 UI + gate false;**R391.2 翻 true**)。
 *
 * ✅ 取證(2026-09-25 當天,owner 轉交 BE handoff「D9 分鐘級」後逐欄對帳):
 *   1. BE tag **`dev-update-v22-122`** 含 `points_valid_minutes`(redeem 四檔;branch
 *      `3e182862` feat(redeem): D9)—— dev 靠 tag 生效、main 未併(同 R277/R391 型;
 *      snapshot 待 BE 併 main 後 `contract:dump` 補齊)。驗到 tag 內容層,HTTP 未驗。
 *   2. wire 逐欄對過 `schemas.py`(v22-122):request `points_valid_minutes: int|None,
 *      ge=1 le=525600`;⚠️ **互斥看 `model_fields_set`** —— minutes 非 null 且 days
 *      **鍵有出現**(哪怕值 = 30)→ 422,所以 FE 送 minutes 時**連 days 鍵都不能帶**
 *      (http 層本來就是「undefined 不上 wire」,守住);回應/清單/兌換多
 *      `points_valid_minutes` / `points_expire_at`;`/me` `expiring[]` 多 `expires_at`。
 *   3. 顯示規則(BE D9 全站統一):失效瞬間換算台北 = 00:00 → 天級顯日期;否則顯
 *      M/D HH:mm —— 單點 = [pointsExpiryDisplay](../lib/pointsExpiryDisplay.ts)。
 *
 * 要退場(BE 下架分鐘級)翻回 `false`:「分鐘 / 小時」單位消失、自訂只剩「天」;
 * `redeemGate.test` 的 production 鎖與 `redeemAdminMinutesOff.test` 的 off 分支一起動。
 */
export const REDEEM_POINTS_VALID_MINUTES_SUPPORTED = true;
